import React, { useMemo, useState } from "react";
import axios from "axios";
import {
    FiAlertCircle,
    FiCheckCircle,
    FiChevronDown,
    FiChevronUp,
    FiClock,
    FiCreditCard,
    FiFileText,
    FiRefreshCw,
    FiSearch,
    FiSmartphone,
    FiUser,
} from "react-icons/fi";
import "../Dashboard.css";
import "../styles/VerifyClient.css";

import Header from "../components/Header";
import Footer from "../components/Footer";
import ScrollToTop from "../components/ScrollTop";
import PreloadFetch from "../components/PreloadFetch";
import { useAuth } from "../provider/authProvider";

const PEN = new Intl.NumberFormat("es-PE", {
    style: "currency",
    currency: "PEN",
    minimumFractionDigits: 2,
});

const money = (value) => PEN.format(Number(value || 0));
const text = (value) => value || "—";

const statusConfig = {
    paid: { label: "Pagado", className: "isPaid" },
    pending: { label: "Pendiente", className: "isPending" },
    overdue: { label: "Vencido", className: "isOverdue" },
};

function SummaryCards({ summary }) {
    const values = [
        ["Cuotas pagadas", summary?.paidInstallments || 0, FiCheckCircle, "success"],
        ["Cuotas pendientes", summary?.pendingInstallments || 0, FiClock, "pending"],
        ["Cuotas vencidas", summary?.overdueInstallments || 0, FiAlertCircle, "danger"],
        ["Saldo pendiente", money(summary?.pendingAmount), FiCreditCard, "money"],
    ];

    return (
        <div className="vcSummaryGrid">
            {values.map(([label, value, Icon, tone]) => (
                <article className={`vcSummaryCard vcSummaryCard--${tone}`} key={label}>
                    <span className="vcSummaryIcon"><Icon /></span>
                    <div><small>{label}</small><strong>{value}</strong></div>
                </article>
            ))}
        </div>
    );
}

function Progress({ value = 0 }) {
    const safeValue = Math.max(0, Math.min(100, Number(value || 0)));
    return (
        <div className="vcProgress" aria-label={`Progreso de pagos ${safeValue}%`}>
            <div className="vcProgressHeader"><span>Progreso de pagos</span><strong>{safeValue}%</strong></div>
            <div className="vcProgressTrack"><span style={{ width: `${safeValue}%` }} /></div>
        </div>
    );
}

function ScheduleTable({ title, subtitle, schedule = [], accent = "credit" }) {
    return (
        <section className={`vcPanel vcPanel--${accent}`}>
            <header className="vcPanelHeader">
                <div>
                    <span className="vcEyebrow">Cronograma completo</span>
                    <h3>{title}</h3>
                    <p>{subtitle}</p>
                </div>
                <span className="vcCountBadge">{schedule.length} cuotas</span>
            </header>

            <div className="vcTableScroll">
                <table className="vcScheduleTable">
                    <thead>
                        <tr>
                            <th>Cuota</th><th>Vencimiento</th><th>Monto</th><th>Fecha pagada</th><th>Registrado por</th><th>Estado</th>
                        </tr>
                    </thead>
                    <tbody>
                        {schedule.length ? schedule.map((item) => {
                            const state = statusConfig[item.status] || statusConfig.pending;
                            return (
                                <tr key={`${accent}-${item.id}-${item.installment}`}>
                                    <td data-label="Cuota"><strong>#{item.installment}</strong></td>
                                    <td data-label="Vencimiento">{text(item.date)}</td>
                                    <td data-label="Monto"><strong>{money(item.amount)}</strong></td>
                                    <td data-label="Fecha pagada">{text(item.paidDate)}</td>
                                    <td data-label="Registrado por">{text(item.registeredBy)}</td>
                                    <td data-label="Estado"><span className={`vcStatus ${state.className}`}>{state.label}</span></td>
                                </tr>
                            );
                        }) : (
                            <tr className="vcEmptyRow">
                                <td colSpan="6">No existe un cronograma registrado para este financiamiento.</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </section>
    );
}

function DetailItem({ label, value }) {
    return <div className="vcDetailItem"><span>{label}</span><strong>{text(value)}</strong></div>;
}

function CreditBlock({ credit, historical = false }) {
    if (!credit) return null;
    return (
        <section className={`vcSectionBlock ${historical ? "vcSectionBlock--history" : ""}`}>
            <div className="vcSectionTitle">
                <div className="vcSectionIcon vcSectionIcon--credit"><FiSmartphone /></div>
                <div>
                    <span className="vcEyebrow">{historical ? "Renovación anterior" : "Producto principal"}</span>
                    <h2>{historical ? `Crédito finalizado #${credit.id}` : "Crédito MovilesPay activo"}</h2>
                    <p>{text(credit.phoneName)} · {credit.statusName}</p>
                </div>
            </div>

            <SummaryCards summary={credit.summary} />
            <Progress value={credit.summary?.progress} />

            <div className="vcDetailsGrid">
                <DetailItem label="Fecha de contrato" value={credit.contractDate} />
                <DetailItem label="Sede" value={credit.sedeName} />
                <DetailItem label="Asesor" value={credit.advisorName} />
                <DetailItem label="IMEI" value={credit.imei} />
                <DetailItem label="Frecuencia" value={credit.paymentFrequency} />
                <DetailItem label="Precio del equipo" value={money(credit.phonePrice)} />
                <DetailItem label="Inicial" value={money(credit.initialPrice)} />
                <DetailItem label="Total financiamiento" value={money(credit.totalAmountFinance)} />
            </div>

            <ScheduleTable
                title={historical ? `Cronograma histórico del crédito #${credit.id}` : "Cronograma del crédito activo"}
                subtitle={historical ? "Conserva todas las cuotas y pagos de esta renovación finalizada." : "Incluye cuotas pagadas, pendientes y vencidas del crédito vigente."}
                schedule={credit.schedule}
                accent={historical ? "history" : "credit"}
            />
        </section>
    );
}

function FmBlock({ fm, index }) {
    return (
        <section className="vcSectionBlock vcSectionBlock--fm">
            <div className="vcSectionTitle">
                <div className="vcSectionIcon vcSectionIcon--fm"><FiCreditCard /></div>
                <div>
                    <span className="vcEyebrow">Financiamiento mixto #{index + 1}</span>
                    <h2>FM {fm.sourceName}</h2>
                    <p>{text(fm.phoneName)} · {fm.statusName}</p>
                </div>
            </div>

            <SummaryCards summary={fm.summary} />
            <Progress value={fm.summary?.progress} />

            <div className="vcDetailsGrid">
                <DetailItem label="Fecha de registro" value={fm.contractDate} />
                <DetailItem label="Fuente" value={fm.sourceName} />
                <DetailItem label="Sede" value={fm.sedeName} />
                <DetailItem label="Asesor" value={fm.advisorName} />
                <DetailItem label="Frecuencia" value={fm.paymentFrequencyName} />
                <DetailItem label="Monto total FM" value={money(fm.amount)} />
                <DetailItem label="Cuota FM" value={money(fm.fee)} />
                <DetailItem label="Cuota total combinada" value={money(Number(fm.fee || 0) + Number(fm.amountSource || 0))} />
            </div>

            <ScheduleTable
                title={`Cronograma FM ${fm.sourceName}`}
                subtitle="La tabla siempre permanece visible y muestra lo pagado, pendiente y vencido."
                schedule={fm.schedule}
                accent="fm"
            />
        </section>
    );
}

function HistorySection({ credits = [] }) {
    const [open, setOpen] = useState(true);
    return (
        <section className="vcHistory">
            <button className="vcHistoryHeader" type="button" onClick={() => setOpen((value) => !value)}>
                <span><FiRefreshCw /><span><small>Renovaciones</small><strong>Historial de créditos finalizados ({credits.length})</strong></span></span>
                {open ? <FiChevronUp /> : <FiChevronDown />}
            </button>
            {open && (
                <div className="vcHistoryBody">
                    {credits.length ? credits.map((credit) => <CreditBlock key={credit.id} credit={credit} historical />) : (
                        <div className="vcAlert vcAlert--neutral"><FiFileText /><span>El cliente todavía no registra créditos finalizados.</span></div>
                    )}
                </div>
            )}
        </section>
    );
}

function VerifyClient(props) {
    const { user } = useAuth();
    const [dni, setDni] = useState("");
    const [result, setResult] = useState(null);
    const [preload, setPreload] = useState(false);
    const [error, setError] = useState("");
    const [searched, setSearched] = useState(false);

    const totalDebt = useMemo(() => Number(result?.totals?.pendingAmount || 0), [result]);

    const verifyClient = async (event) => {
        event?.preventDefault();
        const dniClean = dni.replace(/\D/g, "").slice(0, 8);
        if (dniClean.length !== 8) {
            setError("Ingrese un DNI válido de 8 dígitos.");
            return;
        }

        try {
            setPreload(true);
            setError("");
            setSearched(true);
            setResult(null);

            const { data } = await axios.post(
                `${process.env.REACT_APP_API}/verify-client/search`,
                { dni: dniClean },
                { headers: { Authorization: `Bearer ${localStorage.getItem("token")}`, "Content-Type": "application/json" } }
            );

            if (data?.msg !== "success") throw new Error(data?.error || "No se pudo consultar el cliente.");
            setResult(data.data);
        } catch (requestError) {
            setError(requestError?.response?.data?.error || requestError?.message || "No se pudo consultar el cliente.");
        } finally {
            setPreload(false);
        }
    };

    return (
        <div>
            {preload && <PreloadFetch />}
            <ScrollToTop />
            <Header user={user} namePage={props.namePage} />

            <section className="sectionBody sectionBodyVerifyClient vcPage">
                <div className="containerCustom containerBody vcContainer">
                    <section className="vcHero">
                        <div className="vcHeroCopy">
                            <span className="vcEyebrow">Consulta global</span>
                            <h1>Estado financiero completo del cliente</h1>
                            <p>Una sola búsqueda por DNI consulta créditos activos, todos los FM y cada renovación finalizada.</p>
                        </div>
                        <form className="vcSearch" onSubmit={verifyClient}>
                            <label htmlFor="verify-client-dni">Documento de identidad</label>
                            <div className="vcSearchControl">
                                <FiSearch />
                                <input id="verify-client-dni" value={dni} onChange={(event) => { setDni(event.target.value.replace(/\D/g, "").slice(0, 8)); setError(""); }} placeholder="Ingrese 8 dígitos" inputMode="numeric" autoComplete="off" maxLength={8} />
                                <button type="submit" disabled={preload || dni.length !== 8}>Consultar</button>
                            </div>
                            <small>La consulta es global para todos los créditos y FM registrados con el DNI.</small>
                        </form>
                    </section>

                    {error && <div className="vcAlert vcAlert--error"><FiAlertCircle /><span>{error}</span></div>}

                    {result?.found && (
                        <>
                            <section className="vcClientHeader">
                                <div className="vcAvatar"><FiUser /></div>
                                <div className="vcClientIdentity">
                                    <span className="vcEyebrow">Cliente encontrado</span>
                                    <h2>{result.customer?.fullName || `DNI ${result.dni}`}</h2>
                                    <div className="vcClientMeta"><span>DNI {result.dni}</span><span>{text(result.customer?.phone)}</span><span>{text(result.customer?.email)}</span></div>
                                </div>
                                <div className="vcDebtTotal"><small>Deuda total vigente</small><strong>{money(totalDebt)}</strong><span>Crédito activo + FM activos</span></div>
                            </section>

                            <section className="vcTopStats vcTopStats--four">
                                <article><FiFileText /><div><small>Créditos finalizados</small><strong>{result.totals?.completedCreditsCount || 0}</strong></div></article>
                                <article><FiSmartphone /><div><small>Crédito actual</small><strong>{result.activeCredit ? "Activo" : "Sin crédito"}</strong></div></article>
                                <article><FiCreditCard /><div><small>FM encontrados</small><strong>{result.totals?.fmCount || 0}</strong></div></article>
                                <article><FiAlertCircle /><div><small>Deuda vencida</small><strong>{money(result.totals?.overdueAmount)}</strong></div></article>
                            </section>

                            {result.activeCredit ? <CreditBlock credit={result.activeCredit} /> : <div className="vcAlert vcAlert--neutral"><FiSmartphone /><span>El cliente no tiene un crédito MovilesPay activo.</span></div>}

                            <section className="vcGroupHeading"><span className="vcEyebrow">Financiamientos mixtos</span><h2>FM encontrados ({result.fms?.length || 0})</h2><p>Se muestran PayJoy, MovilesPay, Efectiva, CrediSmart y Alo Credit de forma independiente.</p></section>
                            {result.fms?.length ? result.fms.map((fm, index) => <FmBlock key={fm.id} fm={fm} index={index} />) : <div className="vcAlert vcAlert--neutral"><FiCreditCard /><span>El cliente no registra financiamientos mixtos.</span></div>}

                            <HistorySection credits={result.completedCredits || []} />
                        </>
                    )}

                    {searched && !preload && result && !result.found && <div className="vcAlert vcAlert--neutral"><FiSearch /><span>No se encontraron créditos ni FM para el DNI consultado.</span></div>}
                </div>
            </section>
            <Footer />
        </div>
    );
}

export default VerifyClient;
